import { ChangeDetectorRef, Directive, inject, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { Basket } from '@df/session/api/basket/basket';
import { Customer } from '@df/session/api/customer';
import { PriceService, TaxArea, TaxMode } from '@df/ui/price/price.service';
import { Nexus } from '@tomandco/nexus-api-types';
import omit from 'lodash-es/omit';
import { BehaviorSubject, Subscription, SubscriptionLike } from 'rxjs';

export interface IBasketTotalsItem {
    label: string;
    value: number;
    comment?: string;
    id: string;
}

@Directive()
export abstract class BasketTotalsComponentDef implements OnInit, OnDestroy {
    totals: IBasketTotalsItem[] = [];

    totalsMap: { [totalId: string]: number } = {};

    protected totals$ = new BehaviorSubject<Nexus.Lib.Basket.Totals | undefined>(undefined);

    @Input()
    allowEmptySubtotal = false;

    @Input()
    alwaysDisplaySubtotal = false;

    /**
     * Context is needed to define tax area
     */
    @Input()
    context: 'basket' | 'order' = 'basket';

    /**
     * Use with order mode
     */
    @Input()
    source?: Nexus.Lib.Basket.Totals;

    protected subscriptions = new Subscription();

    protected update!: () => void;

    protected dfEventService = inject(DfEventService);
    protected requestService = inject(RequestService);
    protected priceService = inject(PriceService);
    protected basket = inject(Basket);

    protected removeCouponStatus = new Status();

    private basketSubscription?: SubscriptionLike;

    /**
     * Custom order
     */
    protected order?: string[];

    private initiated = false;

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected app: App,
        protected customer: Customer,
        protected timeoutService: TimeoutService
    ) {}

    ngOnInit(): void {
        this.update = this.timeoutService.debounce(() => this._update(), 100);
        this.setTotalsSubscription();
        this.initiated = true;
        this.updateSourceSubscription();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['context']) {
            if (this.initiated) {
                this.updateSourceSubscription();
            }
        }
        if (changes['source']) {
            if (this.source) {
                this.totals$.next(this.source);
            }
        }
    }

    private updateSourceSubscription() {
        this.basketSubscription?.unsubscribe();
        if (this.context === 'basket' && !this.source) {
            this.basketSubscription = this.dfEventService.success(event => {
                if (event.id === 'basketDataChange') {
                    this.totals$.next(this.basket.data?.totals);
                }
            });
            this.totals$.next(this.basket.data?.totals);
        }
    }

    private setTotalsSubscription() {
        this.subscriptions.add(
            this.totals$.subscribe(totals => {
                this._update();
            })
        );
    }

    protected _update(): void {
        const basketTotals = this.totals$.value;
        const totals: IBasketTotalsItem[] = [];
        const totalsMap: { [totalId: string]: number } = {};

        if (basketTotals) {
            const isTaxIncludedInBasketPrices =
                this.context === 'order' ? this.priceService.isTaxIncludedInOrderPrices() : this.priceService.isTaxIncludedInBasketPrices();
            const isTaxIncludedInShippingPrices = this.priceService.isTaxIncludedInShippingPrices();
            const shouldTaxBeIncludedInBasketPrices =
                this.context === 'order'
                    ? this.priceService.shouldTaxBeIncludedInOrderPrices()
                    : this.priceService.shouldTaxBeIncludedInBasketPrices();
            const shouldTaxBeIncludedInShippingPrices = this.priceService.shouldTaxBeIncludedInShippingPrices();
            const shouldTaxBreakdownBeShownForBasket = this.priceService.shouldTaxBreakdownBeShownForBasket();

            const basketDisplayTaxMode = shouldTaxBeIncludedInBasketPrices ? TaxMode.including : TaxMode.excluding;
            const shippingDisplayTaxMode = shouldTaxBeIncludedInShippingPrices ? TaxMode.including : TaxMode.excluding;

            const subtotal = this.priceService.getSubtotal(
                basketTotals,
                this.context === 'order' ? TaxArea.order : TaxArea.basket,
                basketDisplayTaxMode
            );
            const discount = this.priceService.getDiscount(
                basketTotals,
                this.context === 'order' ? TaxArea.order : TaxArea.basket,
                basketDisplayTaxMode
            );
            const giftcards = this.getGiftcardDiscount(basketDisplayTaxMode) * -1;

            if (subtotal || this.allowEmptySubtotal) {
                totals.push({ label: this.getSubtotalLabel(basketDisplayTaxMode), id: 'subtotal', value: subtotal });
                totalsMap['subtotal'] = subtotal;
            }

            if (discount) {
                totals.push({ label: this.getDiscountLabel(basketDisplayTaxMode), id: 'discount', value: discount });
                totalsMap['discount'] = discount;
            }

            if (giftcards) {
                totals.push({ label: this.getGiftcardsLabel(basketDisplayTaxMode), id: 'giftcards', value: giftcards });
                totalsMap['giftcards'] = giftcards;
            }

            if (this.withShipping) {
                const shippingValue = this.getShippingValue(shippingDisplayTaxMode);
                totals.push({
                    label: this.getShippingLabel(shippingDisplayTaxMode),
                    id: 'shipping',
                    value: shippingValue
                });
                totalsMap['shipping'] = shippingValue;
            }

            /**
             * NOTE from Maurice
             *
             * It seems that we have a problem with how we process and pass configs for tax.
             * I think that we pass wrong setting about if the tax is included or excluded in basket totals
             * or we are missing some configurations.
             *
             * For now this should be fine, but ultimately we may need to fix this.
             */
            // v1 only
            // if (basketTotals.tax && (!isTaxIncludedInBasketPrices || !isTaxIncludedInShippingPrices)) {
            if (shouldTaxBreakdownBeShownForBasket) {
                const basketTax = basketTotals.tax || 0;
                const shippingTax = basketTotals.shipping_tax || 0;
                const subtotalTax = basketTotals.subtotal_tax || 0;
                const value =
                    Math.round(
                        100 *
                            (!isTaxIncludedInBasketPrices && !isTaxIncludedInShippingPrices
                                ? basketTax
                                : !isTaxIncludedInBasketPrices
                                  ? basketTax - shippingTax
                                  : basketTax - subtotalTax - (this.withShipping ? 0 : shippingTax))
                    ) / 100;

                if (value) {
                    totals.push({ label: this.getTaxLabel(), id: 'tax', value });
                    totalsMap['tax'] = value;
                }
            }

            const grandTotal = this.getTotalValue();
            totals.push({ label: this.getTotalLabel(), id: 'total', value: grandTotal });
            totalsMap['total'] = grandTotal;

            this.addCustomTotals(totals, totalsMap);
        }

        const keys = Object.keys(totalsMap);
        if (!this.alwaysDisplaySubtotal && keys.length === 2 && keys.includes('subtotal') && keys.includes('total')) {
            this.totalsMap = omit(totalsMap, 'subtotal');
            this.setTotals(totals.filter(i => i.id !== 'subtotal'));
        } else {
            this.totalsMap = totalsMap;
            this.setTotals(totals);
        }
    }

    protected addCustomTotals(totals: IBasketTotalsItem[], totalsMap: { [totalId: string]: number }): void {}

    protected setTotals(totals: IBasketTotalsItem[]): void {
        const order = this.order;
        if (order) {
            const sortedTotals = order.map(i => totals.find(t => t.id === i)).filter(Boolean) as IBasketTotalsItem[];
            const otherTotals = totals.filter(t => !order.includes(t.id));
            const totalIndex = sortedTotals.findIndex(i => i.id === 'total');
            sortedTotals.splice(totalIndex === -1 ? sortedTotals.length : totalIndex, 0, ...otherTotals);
            this.totals = sortedTotals;
        } else {
            this.totals = totals;
        }
        this.detectChanges();
    }

    protected getSubtotalLabel(taxMode?: TaxMode): string {
        return 'Subtotal';
    }

    /**
     * @deprecated Typoe, use getSubtatalLabel instead
     * remove this function after GR is fixed
     */
    protected getSubtotlaLabel(taxMode?: TaxMode): string {
        return this.getSubtotalLabel(taxMode);
    }

    protected getDiscountLabel(taxMode?: TaxMode): string {
        return 'Discount';
    }

    protected getGiftcardsLabel(taxMode?: TaxMode): string {
        return 'Gift Cards';
    }

    protected getShippingLabel(taxMode?: TaxMode): string {
        return 'Delivery';
    }

    protected getTaxLabel(): string {
        return this.priceService.shouldTaxBeIncludedInBasketPrices() ? 'Tax (Included)' : 'Tax';
    }

    protected getTotalLabel(): string {
        return this.withShipping ? 'Order total' : 'Order total (Excl. delivery)';
    }

    protected getShippingValue(taxMode: TaxMode): number {
        const totals = this.totals$.value;
        const sourceDataTaxArea = this.context === 'order' ? TaxArea.order : TaxArea.basket;
        return this.priceService.getShipping(totals, sourceDataTaxArea, taxMode);
    }

    protected getTotalValue(): number {
        return this.totals$.value?.grand_total || 0;
    }

    private getGiftcardDiscount(taxMode: TaxMode = TaxMode.skip): number {
        const totals = this.totals$.value;
        const sourceDataTaxArea = this.context === 'order' ? TaxArea.order : TaxArea.basket;

        const giftcards = this.priceService.getTotal(totals, sourceDataTaxArea, 'giftcards', 'discount_tax', taxMode);
        if (giftcards) {
            return giftcards;
        }
        const amgiftcard = this.priceService.getTotal(totals, sourceDataTaxArea, 'amgiftcard', 'discount_tax', taxMode);
        if (amgiftcard) {
            return -1 * amgiftcard;
        }
        return 0;
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    protected get withShipping(): boolean {
        if (this.context === 'order') {
            return true;
        }
        return !!this.basket.data?.shippingMethod || !!this.basket.shipping;
    }

    trackById(index: number, total: IBasketTotalsItem): string {
        return total.id;
    }

    /**
     * Sends remove coupon request
     * Takes coupon code to remove as param - if not defined we will take this.coupon
     * that should work for one-coupon websited
     *
     * @param  {string} code Coupon code to remove
     * @return {promise}
     */
    protected removeCoupon() {
        return this.requestService.send(this.removeCouponStatus, () =>
            this.requestService.nexus.coupon.remove({
                coupon: this.basket.data.coupon as string
            })
        );
    }
}
